import { useCallback, useEffect, useMemo, useRef } from "react";
import { NotificationType, useNotification } from "@gelsenwasser/react";
import { FormikErrors } from "formik";
import debounce from "lodash.debounce";
import L, { LatLng } from "leaflet";
import { defaultMapCenter, VALID_ADDRESSTYPES, Projektstandort } from "domain/Types";
import { useStandortStoreActions } from "stores/useStandortStore";
import { kommunenJsonData } from "common/kommuneUtil";
import { useKommune } from "domain/KommunenContext";
import { useGetAddress, useGetPosition } from "services/queries/osmQueries";
import { useTurf } from "common/hooks/useTurf";
import { log } from "common/Logger";
import GwLogo from "../../../assets/gw_logo.png";
import { QueryClient, useQueryClient } from "react-query";

const debouncedStandort = debounce((standort: Projektstandort, queryClient: QueryClient) => {
  queryClient.cancelQueries("Standort");
  return standort;
}, 750);

type UseLocationSearchProps<T> = {
  projektstandort: Projektstandort;
  projektstandortValueName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<T>>;
  skipLocationSearch?: boolean;
};

const checkIfStandortIsComplete = (standort: Projektstandort): boolean => {
  return !!(
    standort.strasse &&
    standort.hausnummer &&
    standort.ort &&
    standort.plz) ||
    !!(standort.flur && standort.flurstueck);
}

export const useLocationSearch = <T>({
  projektstandort,
  projektstandortValueName,
  setFieldValue,
}: UseLocationSearchProps<T>) => {
  // stores
  const { setIsValidStandort } = useStandortStoreActions();

  // hooks
  const { kommune, switchKommune, switchBranding } = useKommune();
  const { addNotification } = useNotification();
  const { checkIsWithinBoundaries } = useTurf();
  const queryClient = useQueryClient();

  // states
  const markerRef = useRef<L.Marker>(null);
  //   const initiallyLoaded = useRef(false);
  const locationBackup = useRef<{ lat: number; lng: number }>();
  const previousAddress = useRef<{ strasse: string; hausnummer: string; plz: string; ort: string }>({
    strasse: "",
    hausnummer: "",
    plz: "",
    ort: "",
  });

  // parameter
  // queries and mutations
  const position =
    projektstandort.latitude && projektstandort.longitude
      ? new LatLng(projektstandort.latitude, projektstandort.longitude)
      : undefined;

  const {
    data: positionLookup,
    isFetched: isPositionFetched,
    isLoading: isPositionLoading,
  } = useGetPosition(
    debouncedStandort(projektstandort, queryClient),
    !projektstandort.karteAktiv && (!!projektstandort.hausnummer || !!projektstandort.strasse || !!projektstandort.plz)
  );
  const { data: addressLookup } = useGetAddress(
    position ?? defaultMapCenter,
    !!projektstandort.karteAktiv && !!position
  );

  // effects
  useEffect(() => {
    // default ort nach Auswahl der Kommune
    log.debug(
      {
        obj: {
          kommune: kommune?.kommunenName,
          lat: kommune?.lat,
          lng: kommune?.lng,
        },
      },
      "Selected Kommune"
    );
    if (!projektstandort.ort) {
      setFieldValue("projektstandort.ort", kommune?.kommunenName ?? "", false);
      setFieldValue("projektstandort.name", kommune?.name ?? "", false);
    }
  }, []);

  useEffect(() => {
    // hier wird die der Marker auf der Karte positioniert, wenn eine Adresse eingegeben wurde
    log.debug(
      {
        obj: {
          karteAktiv: projektstandort.karteAktiv,
          positionLookup,
          previousAddress: previousAddress.current,
        },
      },
      "running effect for positionLookup"
    );
    if (!positionLookup) {
      return;
    }

    if (!positionLookup.position || !positionLookup.addresstype) {
      setIsValidStandort(false);
      return;
    }

    if (!checkIsWithinBoundaries(kommune, positionLookup.position)) {
      resetToLastStandort(`Der gewählte Standort wird leider nicht versorgt.`);
    } else {
      locationBackup.current = positionLookup.position;
      console.log("positionlookup addressType", positionLookup.addresstype);
      setIsValidStandort(VALID_ADDRESSTYPES.includes(positionLookup.addresstype));
      if (
        !projektstandort.karteAktiv &&
        (previousAddress.current.strasse !== projektstandort.strasse ||
          previousAddress.current.hausnummer !== projektstandort.hausnummer ||
          previousAddress.current.plz !== projektstandort.plz ||
          previousAddress.current.ort !== projektstandort.ort)
      ) {
        updateProjektPosition(projektstandort, positionLookup.position);
      }
    }
  }, [positionLookup]);

  useEffect(() => {
    // hier wird die Adresse gespeichert, wenn der Marker bewegt wurde
    log.debug(
      {
        obj: {
          karteAktiv: projektstandort.karteAktiv,
          addressLookup,
        },
      },
      "running effect for addressLookup"
    );
    if (projektstandort.karteAktiv && addressLookup) {
      setIsValidStandort(projektstandort.karteAktiv && !!(addressLookup.address?.house_number));
      const cityOrTown = addressLookup.address.city
        ? addressLookup.address.city
        : addressLookup.address.town
          ? addressLookup.address.town
          : addressLookup.address.village;
      if (kommune?.kommunenName !== cityOrTown && !kommune?.acceptableKommunenNamen?.includes(cityOrTown)) {
        log.warn(`select city does not match kommune ${kommune?.kommunenName}`);

        // gibt es eine passende Kommune, wenn ja, dann die Komune wechseln
        const alternativeKommune = kommunenJsonData.find((k) => k.kommunenName === cityOrTown);
        if (alternativeKommune) {
          log.warn(`switching kommune to ${alternativeKommune.kommunenName}`);
          switchKommune(alternativeKommune);
          switchBranding({
            datenschutzLink: alternativeKommune.datenschutzLink || "",
            gesellschaftsName: alternativeKommune.gesellschaftsName || "",
            impressumLink: alternativeKommune.impressumLink || "",
            kontaktEmail: alternativeKommune.kontaktEmail || "",
            logo: `./logos/${alternativeKommune.logo}` || GwLogo,
          });
          if (!checkIsWithinBoundaries(alternativeKommune, position)) {
            resetToLastStandort(`Der gewählte Standort wird leider nicht versorgt.`);
            return;
          }
        } else {
          // reset auf die zuletzt bekannten Koordinaten
          resetToLastStandort(`"${cityOrTown}" passt nicht zu der ausgewählten Kommune "${kommune?.kommunenName}"`);
          return;
        }
      } else if (!checkIsWithinBoundaries(kommune, position)) {
        resetToLastStandort(`Der gewählte Standort wird leider nicht versorgt.`);
        return;
      }

      const neuerStandort = {
        ...projektstandort,
        strasse: addressLookup.address.road ?? "",
        hausnummer: addressLookup.address.house_number ?? "",
        plz: addressLookup.address.postcode ?? "",
        ort: cityOrTown ?? "",
      };

      setFieldValue("projektstandort", neuerStandort, false);
      // hier die aktuellen Koordinaten zwischenspeichern
      locationBackup.current = { lat: neuerStandort.latitude, lng: neuerStandort.longitude };
      // adresse merken
      previousAddress.current = {
        strasse: neuerStandort.strasse,
        hausnummer: neuerStandort.hausnummer,
        plz: neuerStandort.plz,
        ort: neuerStandort.ort,
      };
    } else {
      setIsValidStandort(false);
    }
  }, [addressLookup, projektstandort.karteAktiv]);

  useEffect(() => {
    // wenn karte aktiv ist und die Hausnummer geändert wird,
    // dann prüfen ob Adresse komplett ist und Standort als valide markieren
    if (projektstandort.karteAktiv) {
      const isComplete = checkIfStandortIsComplete(projektstandort);
      log.debug(
        {
          obj: {
            karteAktiv: projektstandort.karteAktiv,
            hausnummer: projektstandort.hausnummer,
            flur: projektstandort.flur,
            flurstueck: projektstandort.flurstueck,
            isComplete,
          },
        }, "running effect for hausnummer change when karteAktiv");
      // Hausnummer, flur und flurstück als neuen Standort setzen
      const neuerStandort = {
        ...projektstandort,
        hausnummer: projektstandort.hausnummer,
        flur: projektstandort.flur,
        flurstueck: projektstandort.flurstueck,
      };
      setFieldValue("projektstandort", neuerStandort, false);
      // adresse merken
      previousAddress.current = {
        ...previousAddress.current,
        hausnummer: neuerStandort.hausnummer,
      };
      
      setIsValidStandort(isComplete);
    }
  }, [projektstandort.karteAktiv, projektstandort.hausnummer, projektstandort.flur, projektstandort.flurstueck]);
  
  const resetToLastStandort = (notificationMsg: string) => {
    const alterStandort = {
      ...projektstandort,
      latitude: locationBackup.current?.lat,
      longitude: locationBackup.current?.lng,
    };
    addNotification(notificationMsg, "Außerhalb des Versorgungsgebiets", NotificationType.Alert);
    setFieldValue("projektstandort", alterStandort, false);
  };

  // data
  // handler
  const updateProjektPosition = useCallback(
    (projektstandort: Projektstandort, position?: LatLng) => {
      const neuerStandort = {
        ...projektstandort,
        latitude: position?.lat,
        longitude: position?.lng,
      };
      log.debug({ obj: neuerStandort }, "updating projektstandort");
      setFieldValue(projektstandortValueName, neuerStandort, false);
    },
    [setFieldValue]
  );
  const eventHandlers: L.LeafletEventHandlerFnMap = useMemo(
    () => ({
      dragend() {
        if (!projektstandort.karteAktiv) return;
        const marker = markerRef.current;
        if (marker != null) {
          const newPosition = marker.getLatLng();
          log.debug({ obj: newPosition }, "got new position, by dragging the marker");

          updateProjektPosition(projektstandort, newPosition);
        }
      },
    }),
    [projektstandort.karteAktiv]
  );

  const handlePositionClicked = (latLng: LatLng) => {
    if (!projektstandort.karteAktiv) return;
    log.debug({ obj: latLng }, "got new position, by clicking the map");

    updateProjektPosition(projektstandort, latLng);
  };

  return {
    defaultMapCenter,
    markerRef,
    kommune,
    position,
    handlePositionClicked,
    eventHandlers,
    isPositionFetched,
    isPositionLoading,
  } as const;
};
